import { ChangeEvent, useRef, useState } from "react"

import { AnimatePresence, motion } from "framer-motion"

import { toBase64 } from "$/utils/functions"

import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { WarningMessage } from "@/shared/WarningMessage"
import Avatar from "@/shared/avatar/Avatar"

import "./style.scss"

type Props = {
    isAccessible?: boolean
    defaultPhoto?: string
    disabled?: boolean
    onChange: (photoBase64: string | ArrayBuffer) => void
}

const ProfilePhotoHandler = ({ isAccessible = false, defaultPhoto = null, onChange, disabled = false }: Props) => {
    const { t }: IUseTranslation = useTranslation()
    const [profilePicture, setProfilePicture] = useState(defaultPhoto)
    const [error, setError] = useState<string | null>(null)
    const labelRef = useRef<HTMLLabelElement | null>(null)

    const handleProfilePhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const imageFile = e.target.files[0]

        if (imageFile) {
            try {
                const base64 = await toBase64(imageFile)
                setProfilePicture(URL.createObjectURL(imageFile))
                onChange(base64)
            } catch {
                setError(t("An error occurred while uploading a photo, please try again later"))
            }
        }
    }

    const onInputFocus = () => {
        if (labelRef.current) {
            labelRef.current.classList.add("addOutline")
        }
    }
    const onInputBlur = () => {
        if (labelRef.current) {
            labelRef.current.classList.remove("addOutline")
        }
    }

    return (
        <label htmlFor="file" className={buildCssClassName({ "cursor-pointer": !disabled })} ref={labelRef}>
            <div className="form-group d-flex align-items-center addPhotoBlock">
                <Avatar width={80} height={80} url={profilePicture} alt={t("common.avatarFallback.alt")} />

                <AnimatePresence initial={false} mode="wait">
                    {isAccessible && (
                        <motion.div
                            key="profile-img-btn"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            transition={{ duration: 0.2, ease: "easeIn" }}
                            className="ml-4"
                        >
                            <input
                                type="file"
                                disabled={disabled}
                                accept="image/png, image/gif, image/jpeg"
                                id="file"
                                onChange={handleProfilePhotoChange}
                                onFocus={onInputFocus}
                                onBlur={onInputBlur}
                            />
                            <p className="brand-link font-extrabold font-sm">
                                &nbsp;&#x2b;&nbsp;{t("Add Profile Image")}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <WarningMessage message={error} />
        </label>
    )
}

export default ProfilePhotoHandler
